/**
 * preMeetPdf — client-side, dependency-light PDF export of a saved
 * pre-meet checklist for the member's records and their Trusted Circle.
 *
 * Layout: single-page A-letter, generous margins, two-column "facts"
 * grid, then a checklist grid, then a safety footer with the Wavlynq
 * Emergency line + crisis link.
 *
 * No network calls; everything renders from the row + supplied profile
 * label.  Triggered straight from the safety panel.
 */
import { PDFDocument, StandardFonts, rgb, type PDFFont, type PDFPage } from "pdf-lib";
import type { PreMeetChecklist } from "@/lib/safety/usePreMeet";

const PAGE_W = 612; // US Letter (pt)
const PAGE_H = 792;
const MARGIN = 54;  // 0.75"
const COL_GAP = 18;
const INK = rgb(0.07, 0.09, 0.15);
const MUTED = rgb(0.42, 0.46, 0.55);
const RULE = rgb(0.86, 0.88, 0.92);
const ACCENT = rgb(0.16, 0.36, 0.86);

interface DrawCtx {
  page: PDFPage;
  font: PDFFont;
  bold: PDFFont;
  y: number;
}

function fmtDate(iso: string | null): string {
  if (!iso) return "Not scheduled";
  const d = new Date(iso);
  return d.toLocaleString(undefined, {
    weekday: "short",
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
}

/** Wrap a string into lines that fit `maxWidth` at the given size. */
function wrap(font: PDFFont, text: string, size: number, maxWidth: number): string[] {
  const words = text.split(/\s+/);
  const lines: string[] = [];
  let current = "";
  for (const w of words) {
    const candidate = current ? `${current} ${w}` : w;
    if (font.widthOfTextAtSize(candidate, size) > maxWidth && current) {
      lines.push(current);
      current = w;
    } else {
      current = candidate;
    }
  }
  if (current) lines.push(current);
  return lines;
}

function drawText(
  ctx: DrawCtx,
  text: string,
  opts: { size?: number; bold?: boolean; color?: ReturnType<typeof rgb>; x?: number },
) {
  const size = opts.size ?? 10;
  const f = opts.bold ? ctx.bold : ctx.font;
  ctx.page.drawText(text, {
    x: opts.x ?? MARGIN,
    y: ctx.y,
    size,
    font: f,
    color: opts.color ?? INK,
  });
}

function drawRule(ctx: DrawCtx, color = RULE) {
  ctx.page.drawLine({
    start: { x: MARGIN, y: ctx.y },
    end: { x: PAGE_W - MARGIN, y: ctx.y },
    thickness: 0.5,
    color,
  });
}

function drawFactCell(
  ctx: DrawCtx,
  x: number,
  width: number,
  label: string,
  value: string,
): number {
  ctx.page.drawText(label.toUpperCase(), {
    x,
    y: ctx.y,
    size: 7.5,
    font: ctx.bold,
    color: MUTED,
  });
  const valueLines = wrap(ctx.font, value, 10.5, width);
  let y = ctx.y - 13;
  for (const line of valueLines) {
    ctx.page.drawText(line, { x, y, size: 10.5, font: ctx.font, color: INK });
    y -= 13;
  }
  return ctx.y - (13 + valueLines.length * 13) - 6; // bottom y after this cell
}

function drawCheckPill(
  ctx: DrawCtx,
  x: number,
  width: number,
  label: string,
  on: boolean,
) {
  const h = 22;
  ctx.page.drawRectangle({
    x,
    y: ctx.y - h + 6,
    width,
    height: h,
    borderColor: on ? ACCENT : RULE,
    borderWidth: 0.6,
    color: on ? rgb(0.93, 0.95, 1) : rgb(1, 1, 1),
  });
  // Square indicator
  ctx.page.drawRectangle({
    x: x + 8,
    y: ctx.y - h + 12,
    width: 9,
    height: 9,
    borderColor: on ? ACCENT : MUTED,
    borderWidth: 0.8,
    color: on ? ACCENT : rgb(1, 1, 1),
  });
  if (on) {
    // simple check glyph using two lines
    ctx.page.drawLine({
      start: { x: x + 9.5, y: ctx.y - h + 16 },
      end: { x: x + 12, y: ctx.y - h + 13 },
      thickness: 1.1,
      color: rgb(1, 1, 1),
    });
    ctx.page.drawLine({
      start: { x: x + 12, y: ctx.y - h + 13 },
      end: { x: x + 16, y: ctx.y - h + 19 },
      thickness: 1.1,
      color: rgb(1, 1, 1),
    });
  }
  ctx.page.drawText(label, {
    x: x + 24,
    y: ctx.y - h + 13,
    size: 9.5,
    font: ctx.font,
    color: on ? INK : MUTED,
  });
}

export interface PreMeetPdfMeta {
  ownerLabel: string;     // "Jordan Rivers" or email
  generatedAt: Date;
}

export async function buildPreMeetChecklistPdf(
  checklist: PreMeetChecklist,
  meta: PreMeetPdfMeta,
): Promise<Blob> {
  const pdf = await PDFDocument.create();
  pdf.setTitle(`Pre-meet checklist · ${checklist.meet_place_label ?? ""}`);
  pdf.setAuthor("Wavlynq");
  pdf.setSubject("Pre-meet safety checklist");
  pdf.setProducer("Wavlynq");
  pdf.setCreator("Wavlynq");

  const page = pdf.addPage([PAGE_W, PAGE_H]);
  const font = await pdf.embedFont(StandardFonts.Helvetica);
  const bold = await pdf.embedFont(StandardFonts.HelveticaBold);

  const ctx: DrawCtx = { page, font, bold, y: PAGE_H - MARGIN };

  // ---- Header ------------------------------------------------------
  drawText(ctx, "WAVLYNQ", { size: 9, bold: true, color: ACCENT });
  ctx.y -= 18;
  drawText(ctx, "Pre-meet checklist", { size: 22, bold: true });
  ctx.y -= 16;
  drawText(
    ctx,
    `For ${meta.ownerLabel} · generated ${meta.generatedAt.toLocaleString()}`,
    { size: 9.5, color: MUTED },
  );
  ctx.y -= 14;

  // ---- "Why Trusted Circle" header summary ------------------------
  // Mirrors the wording on TrustedShareDecisionBadge so the PDF and
  // the in-app badge tell the exact same story at a glance:
  //   "Trusted share waived — curated ✓ · same city ✓ · safety docs ✓."
  // For legacy rows without the decision snapshot, we skip the line
  // entirely (the badge component does the same).
  const headerDecision = checklist.trusted_share_decision;
  if (headerDecision) {
    const yn = (b: boolean) => (b ? "yes" : "no");
    const verdict = headerDecision.waived
      ? "Trusted share waived"
      : "Trusted share required";
    const tail = headerDecision.waived
      ? "curated venue exemption"
      : headerDecision.share_enabled
        ? `satisfied · ${headerDecision.trusted_count} contact${headerDecision.trusted_count === 1 ? "" : "s"}`
        : "unsatisfied at save";
    const summary =
      `${verdict} — ${tail} ` +
      `(curated: ${yn(headerDecision.venue_is_curated)}, ` +
      `same city: ${yn(headerDecision.venue_same_city)}, ` +
      `safety docs: ${yn(headerDecision.venue_has_safety_doc)}).`;
    const summaryColor = headerDecision.waived
      ? rgb(0.1, 0.5, 0.25)
      : rgb(0.65, 0.45, 0.05);
    for (const line of wrap(font, summary, 9.5, PAGE_W - MARGIN * 2)) {
      drawText(ctx, line, { size: 9.5, color: summaryColor });
      ctx.y -= 12;
    }
  }

  ctx.y -= 4;
  drawRule(ctx);
  ctx.y -= 18;

  // ---- Facts grid (2 columns) -------------------------------------
  const colW = (PAGE_W - MARGIN * 2 - COL_GAP) / 2;
  const startY = ctx.y;
  const leftBottom = drawFactCell(
    { ...ctx, y: startY }, MARGIN, colW,
    "When", fmtDate(checklist.meet_at),
  );
  const rightBottom = drawFactCell(
    { ...ctx, y: startY }, MARGIN + colW + COL_GAP, colW,
    "Where", checklist.meet_place_label ?? "—",
  );
  ctx.y = Math.min(leftBottom, rightBottom);

  // Coords row, only if present
  if (checklist.meet_lat != null && checklist.meet_lng != null) {
    const coords = `${checklist.meet_lat.toFixed(5)}, ${checklist.meet_lng.toFixed(5)}`;
    ctx.y = drawFactCell(
      { ...ctx, y: ctx.y }, MARGIN, PAGE_W - MARGIN * 2,
      "Coordinates", coords,
    );
  }

  ctx.y = drawFactCell(
    { ...ctx, y: ctx.y }, MARGIN, PAGE_W - MARGIN * 2,
    "Status", checklist.status,
  );

  ctx.y -= 6;
  drawRule(ctx);
  ctx.y -= 22;

  // ---- Safety preparations ----------------------------------------
  drawText(ctx, "Safety preparations", { size: 12, bold: true });
  ctx.y -= 14;

  const pills: { label: string; on: boolean }[] = [
    { label: "Match is ID-verified", on: checklist.has_id_verified },
    { label: "Video-call done first", on: checklist.has_video_call_done },
    { label: "Safe word agreed", on: checklist.has_safe_word },
    { label: "Transport home arranged", on: checklist.has_transport },
    { label: "Shared with Trusted Circle", on: checklist.shared_with_trusted },
  ];

  // Two columns of pills
  const pillW = (PAGE_W - MARGIN * 2 - COL_GAP) / 2;
  for (let i = 0; i < pills.length; i += 2) {
    const left = pills[i];
    const right = pills[i + 1];
    drawCheckPill(ctx, MARGIN, pillW, left.label, left.on);
    if (right) drawCheckPill(ctx, MARGIN + pillW + COL_GAP, pillW, right.label, right.on);
    ctx.y -= 28;
  }

  ctx.y -= 4;
  drawRule(ctx);
  ctx.y -= 22;

  // ---- Trusted Circle gating decision -----------------------------
  // Wording mirrors <TrustedShareDecisionBadge/> character-for-character so
  // the PDF and in-app badge read identically across all decision states.
  // Bullet separator is U+00B7 ( · ) with a single space on each side; the
  // sub-condition row uses the same ordering and labels as the badge.
  const decision = checklist.trusted_share_decision;
  if (decision) {
    drawText(ctx, "Trusted Circle sharing decision", { size: 12, bold: true });
    ctx.y -= 14;

    // Headline = pill label + " · " + sublabel, matching the badge layout.
    const pillLabel = decision.waived
      ? "Trusted share waived"
      : "Trusted share required";
    const subLabel = decision.waived
      ? "curated venue exemption"
      : decision.share_enabled
        ? `satisfied · ${decision.trusted_count} contact${decision.trusted_count === 1 ? "" : "s"}`
        : "unsatisfied at save";
    const headline = `${pillLabel} · ${subLabel}`;
    const headlineColor = decision.waived
      ? rgb(0.1, 0.5, 0.25)
      : rgb(0.65, 0.45, 0.05);
    for (const line of wrap(font, headline, 10.5, PAGE_W - MARGIN * 2)) {
      drawText(ctx, line, { size: 10.5, color: headlineColor });
      ctx.y -= 14;
    }

    // Sub-conditions row: "curated: yes · same city: no · safety docs: yes"
    // Labels and order match the badge's <Cond/> tooltips exactly.
    const cond = (label: string, ok: boolean) =>
      `${label}: ${ok ? "yes" : "no"}`;
    const condLine = [
      cond("curated", decision.venue_is_curated),
      cond("same city", decision.venue_same_city),
      cond("safety docs", decision.venue_has_safety_doc),
    ].join(" · ");
    for (const line of wrap(font, condLine, 9.5, PAGE_W - MARGIN * 2)) {
      drawText(ctx, line, { size: 9.5, color: MUTED });
      ctx.y -= 12;
    }

    ctx.y -= 6;
    drawRule(ctx);
    ctx.y -= 22;
  }

  // ---- For the Trusted Circle -------------------------------------
  drawText(ctx, "If you're a Trusted Circle contact", { size: 12, bold: true });
  ctx.y -= 14;
  const blurb =
    "This document is a courtesy hand-off. If the meet runs late, the member " +
    "doesn't check in by the agreed time, or anything feels wrong, contact them " +
    "directly first. If you can't reach them, escalate to local emergency services.";
  for (const line of wrap(font, blurb, 10.5, PAGE_W - MARGIN * 2)) {
    drawText(ctx, line, { size: 10.5 });
    ctx.y -= 14;
  }
  ctx.y -= 6;

  // Local-emergency note (we deliberately don't bake in a US-only number)
  drawText(ctx, "Local emergency services:", { size: 10, bold: true });
  ctx.y -= 13;
  drawText(
    ctx,
    "Use the emergency number for the meet's country (e.g. 911 in the US, 112 in the EU/UK).",
    { size: 10, color: MUTED },
  );
  ctx.y -= 18;

  drawText(ctx, "Crisis & support resources:", { size: 10, bold: true });
  ctx.y -= 13;
  drawText(ctx, "https://wavlynq.com/safety", { size: 10, color: ACCENT });

  // ---- Footer ------------------------------------------------------
  page.drawText(
    "Generated by Wavlynq · Keep this document private. Do not redistribute.",
    {
      x: MARGIN,
      y: 28,
      size: 8,
      font,
      color: MUTED,
    },
  );

  const bytes = await pdf.save();
  return new Blob([bytes as BlobPart], { type: "application/pdf" });
}

/** Trigger a browser download for the generated PDF blob. */
export function downloadBlob(blob: Blob, filename: string) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  // Revoke on next tick to keep Safari happy.
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

export function preMeetPdfFilename(c: PreMeetChecklist): string {
  const slug = (c.meet_place_label ?? "pre-meet")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/(^-|-$)/g, "")
    .slice(0, 40) || "pre-meet";
  const dt = c.meet_at ? new Date(c.meet_at).toISOString().slice(0, 10) : "draft";
  return `wavlynq-premeet-${slug}-${dt}.pdf`;
}
